"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Braces,
  ChevronRight,
  Download,
  Loader2,
  MoreVertical,
  Save,
  Settings,
  Share2,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import type { EditorSettings } from "../lib/editor-settings";
import type { SnippetEmbedSettings } from "../types";
import { EditorSettingsPanel } from "./EditorSettingsPanel";
import { ShareSnippetDropdownBody } from "./ShareSnippetDropdownBody";
import { cn } from "@/lib/utils";

const menuItemClass =
  "flex min-h-[44px] cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-white/10 data-[disabled]:opacity-40";

const subTriggerClass =
  "flex min-h-[44px] w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[state=open]:bg-white/10 data-[highlighted]:bg-white/10";

type ToolbarOverflowMenuProps = {
  locale: string;
  savedSnippetId: string | null;
  shareSlug: string | null;
  isPasswordProtected: boolean;
  /** The owner's embed opt-in, threaded to the share panel (`PG-EMBED01`). */
  snippetEmbed?: SnippetEmbedSettings;
  onSaveClick: () => void;
  savePending: boolean;
  saveDisabled: boolean;
  saveDisabledTooltip?: string;
  /** Format Code (`PG-IDE02`); the title already explains an unsupported language. */
  onFormat?: () => void;
  isFormatting?: boolean;
  formatTitle?: string;
  onDownloadFile?: () => void;
  canDownloadSource: boolean;
  editorSettings: EditorSettings;
  onEditorSettingsChange: (next: EditorSettings) => void;
  /** Language SEO pages only — show the guide-visibility toggle in settings. */
  showLanguageGuideToggle?: boolean;
  /** Runtime version + compiler flags (`PG-IDE01`) — same panel as the gear menu. */
  runtimePanel?: ReactNode;
  /** Install Progsity IDE (`PG-PWA01`), present only while the browser offers it. */
  onInstall?: () => void;
  /** Offline (`PG-PWA01`): share is disabled with its reason; save arrives already disabled. */
  offline?: boolean;
  /** Auth + upgrade — pinned below Save/Share/Download/Settings (mobile). */
  renderFooter?: (closeMenu: () => void) => ReactNode;
  className?: string;
};

/**
 * Kebab menu for &lt;768px — Save, Share, Download, Settings (docs/24 §10 responsive toolbar).
 */
export function ToolbarOverflowMenu({
  locale,
  savedSnippetId,
  shareSlug,
  isPasswordProtected,
  snippetEmbed,
  onSaveClick,
  savePending,
  saveDisabled,
  saveDisabledTooltip,
  onFormat,
  isFormatting = false,
  formatTitle,
  onDownloadFile,
  canDownloadSource,
  editorSettings,
  onEditorSettingsChange,
  showLanguageGuideToggle = false,
  runtimePanel,
  onInstall,
  offline = false,
  renderFooter,
  className,
}: ToolbarOverflowMenuProps) {
  const t = useTranslations("playground");
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <DropdownMenu.Root modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t("toolbarMoreActions")}
          title={t("toolbarMoreActions")}
          className={cn(
            "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white/90 transition hover:bg-white/10",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]",
            className,
          )}
        >
          <MoreVertical className="h-5 w-5" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] min-w-[220px] rounded-xl border border-white/10 bg-[#141416] p-1 shadow-2xl"
          sideOffset={6}
          align="end"
          collisionPadding={8}
        >
          <DropdownMenu.Item
            disabled={saveDisabled || savePending}
            title={saveDisabled ? saveDisabledTooltip : undefined}
            className={menuItemClass}
            onSelect={() => {
              onSaveClick();
            }}
          >
            {savePending ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Save className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
            )}
            {t("save")}
          </DropdownMenu.Item>

          {onFormat ? (
            <DropdownMenu.Item
              disabled={isFormatting}
              className={menuItemClass}
              title={formatTitle}
              onSelect={() => {
                onFormat();
              }}
            >
              {isFormatting ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
              ) : (
                <Braces className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
              )}
              {isFormatting ? t("formatting") : t("format")}
            </DropdownMenu.Item>
          ) : null}

          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger
              disabled={!savedSnippetId || offline}
              title={
                offline
                  ? t("offlineShareDisabled")
                  : !savedSnippetId
                    ? t("shareDisabledHint")
                    : undefined
              }
              className={cn(subTriggerClass, (!savedSnippetId || offline) && "opacity-40")}
            >
              <Share2 className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
              <span className="flex-1 text-left">{t("share")}</span>
              <ChevronRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent
                className="z-[210] max-h-[min(70vh,420px)] w-[min(100vw-2rem,380px)] overflow-y-auto rounded-xl border border-white/10 bg-[#141416] p-3 shadow-2xl"
                sideOffset={8}
                alignOffset={-4}
                collisionPadding={8}
              >
                {savedSnippetId ? (
                  <ShareSnippetDropdownBody
                    locale={locale}
                    snippetId={savedSnippetId}
                    initialShareSlug={shareSlug}
                    initialIsPasswordProtected={isPasswordProtected}
                    {...(snippetEmbed ? { initialEmbed: snippetEmbed } : {})}
                  />
                ) : null}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>

          {onDownloadFile ? (
            <DropdownMenu.Item
              disabled={!canDownloadSource}
              className={menuItemClass}
              title={t("downloadFileTitle")}
              onSelect={() => {
                onDownloadFile();
              }}
            >
              <Download className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
              {t("downloadFile")}
            </DropdownMenu.Item>
          ) : null}

          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className={subTriggerClass}>
              <Settings className="h-4 w-4 shrink-0 text-white/80" aria-hidden />
              <span className="flex-1 text-left">{t("settings")}</span>
              <ChevronRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent
                className="z-[210] max-h-[min(70vh,480px)] w-[min(100vw-2rem,280px)] overflow-y-auto rounded-xl border border-white/10 bg-[#141416] p-3 shadow-2xl"
                sideOffset={8}
                alignOffset={-4}
                collisionPadding={8}
              >
                <EditorSettingsPanel
                  editorSettings={editorSettings}
                  onEditorSettingsChange={onEditorSettingsChange}
                  showLanguageGuideToggle={showLanguageGuideToggle}
                  runtimePanel={runtimePanel}
                  onInstall={onInstall}
                />
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>

          {renderFooter ? (
            <div className="px-1 pb-1 pt-0">{renderFooter(() => setMenuOpen(false))}</div>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
